export function countCharacters(str: string, char = '?'): number {
  return str.split('').filter((c) => c === char).length;
}

export function cloneSimpleArray<T extends Array<string | number | boolean | null | undefined>>(
  array: T,
): T {
  return array.map((item) => item) as T;
}

export function isNumberLike(value: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(value);
}

export function isBooleanLike(value: string): boolean {
  return /^(true|false)$/.test(value);
}

export function isInteger(str: string) {
  const n = Math.floor(Number(str));
  return n !== Infinity && String(n) === str && n >= 0;
}

export function convertToPrimitive(value: string): string | number | boolean | null {
  if (isNumberLike(value)) {
    return parseFloat(value);
  } else if (isBooleanLike(value)) {
    return value === 'true';
  } else if (value === 'null') {
    return null;
  }

  return value;
}

export const safeParseInt = (v: string | number): number =>
  typeof v === 'string' ? parseInt(v) : v;

/**
 * Replaces nodejs path.join() for browser compatibility
 *
 * @param paths - Path segments to join
 * @returns Joined path with normalized separators
 *
 * @example
 * ```typescript
 * joinPaths('/api', 'users', '123');
 * // Returns: '/api/users/123'
 *
 * joinPaths('https://api.com/', '/users/', '/123');
 * // Returns: 'https://api.com/users/123'
 *
 * joinPaths('/path\\with\\backslashes');
 * // Returns: '/path/with/backslashes'
 * ```
 */
export function joinPaths(...paths: string[]): string {
  const hasProtocol = (paths[0] ?? '').includes('://');
  const protocolSplit = hasProtocol ? (paths[0] ?? '').split('://') : ['', ''];
  const pathInputs = hasProtocol ? [protocolSplit[1], ...paths.slice(1)] : paths;

  // Normalize the separators to use forward slashes and remove consecutive separators.
  const normalize = (p: string): string => {
    const replacedBackslashes = p.replace(/\\/g, '/');
    return replacedBackslashes.replace(/([^:]\/)\/+/g, '$1');
  };

  // Filter out empty path segments.
  const filteredPaths = pathInputs.filter((p) => p !== '');

  // Join the paths and normalize the separators.
  const joinedPath = filteredPaths.join('/');
  const normalizedPath = normalize(joinedPath);

  return hasProtocol ? `${protocolSplit[0]}://${normalizedPath}` : normalizedPath;
}

export function getPathUntilSegment(fullPath: string, segment: string): string {
  const parts = fullPath.split('/');
  const idx = parts.indexOf(segment);
  if (idx === -1) {
    console.warn(`could not find ${segment} in path ${fullPath}`);
    return fullPath;
  }
  return parts.slice(0, idx + 1).join('/');
}

export const wait = (seconds: number) =>
  new Promise((resolve) => setTimeout(resolve, seconds * 1000));

export function stripPrefix(str: string, prefix: string, caseInsensitive = true) {
  const r = new RegExp(`^${prefix}`, caseInsensitive ? 'i' : undefined);
  if (r.test(str)) return str.slice(prefix.length);
  return str;
}

export function addPrefix(str: string, prefix: string) {
  if (str.startsWith(prefix)) return str;
  return prefix + str;
}

/** Utility function to create a K:V from a list of strings */
export function strEnum<T extends string>(
  o: T[],
): { vals: { [K in T]: K }; has: (v: string) => v is T } {
  const vals = o.reduce((res, key) => {
    res[key] = key;
    return res;
  }, Object.create(null));

  const has = (v: string): v is T => o.includes(v as T);
  return { vals, has };
}

export function strEnumNs<N extends string, T extends string>(
  ns: N,
  o: T[],
): { [K in T]: `${N}.${K}` } {
  return o.reduce((res, key) => {
    res[key] = [ns, key].join('.');
    return res;
  }, Object.create(null));
}

/**
 * @desc Replacing lodash _.omit method
 * @param {T} obj
 * @param {K[]} keys
 * @returns {Omit<T, K>}
 */
export function omit<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
  return Object.entries(obj).reduce(
    (agg, [key, value]) => {
      if (!keys.includes(key as K)) return { ...agg, [key]: value as T[K] };
      return agg;
    },
    {} as Omit<T, K>,
  );
}

/**
 * @desc Simple function for filtering flattened objects without traversal.
 * @param obj
 * @param keys
 */
export function pick<T extends Record<K, unknown>, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Pick<T, K> {
  const result = {} as T;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}

export const parseBoolEnv = (v: number | string | boolean | undefined) =>
  [1, 'true', true].includes(v ?? 0);
